import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	ButtonComponent,
	ButtonIconComponent,
	IconComponent,
	INavHelp,
	ItemComponent,
	ItemGroupComponent,
	LinkComponent,
	ListDetailComponent,
	NavHelpComponent,
	ProgressComponent,
	PwcsLayoutComponent,
	SpinnerComponent,
	TagComponent,
	TileComponent,
	WatermarkComponent,
	DividerComponent,
	FormGroupComponent,
	InputComponent,
} from '@tecalisdev/infinity';
import { timer } from 'rxjs/internal/observable/timer';
import { finalize } from 'rxjs/internal/operators/finalize';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs/internal/Subject';
import { Subscription } from 'rxjs/internal/Subscription';
import { ProgressService } from 'src/app/core/helpers/progress.service';
import { ILoading } from 'src/app/core/models/loading.models';
import { NavHelpAction } from 'src/app/core/models/navHelp.models';
import { AuthService } from 'src/app/core/services/auth.service';
import { LanguageService } from 'src/app/core/services/language.service';
import { LoadingService } from 'src/app/core/services/loading.service';
import { HandsetLandscapeTileComponent } from 'src/app/shared/components/handset-landscape-tile/handset-landscape-tile.component';
import { ModalChangeDeviceComponent } from 'src/app/shared/components/modal-change-device/modal-change-device.component';
import { ModalScanQrComponent } from 'src/app/shared/components/modal-scan-qr/modal-scan-qr.component';
import { ERROR_CODE, ERROR_STATUS } from 'src/app/shared/models/error.models';
import { IMaster } from 'src/app/shared/models/master.models';
import { AudioService } from 'src/app/shared/services/audio.service';
import { CloudLogsService, LogType } from 'src/app/shared/services/cloud-logs.service';
import { ConfigService } from 'src/app/shared/services/config/config.service';
import { DesignService } from 'src/app/shared/services/config/design.service';
import { ErrorService } from 'src/app/shared/services/error.service';
import { IdentitySDKService, OperationType } from 'src/app/shared/services/identity-sdk.service';
import { MasterService } from 'src/app/shared/services/master.service';
import { LanguagePickerUiService } from 'src/app/shared/services/language-picker-ui.service';
import { NavHelpService } from 'src/app/shared/services/nav-help.service';
import {
	MaxFileSizePerDocumentBytes,
	MaxFileSizePerDocumentMB,
	DOCS_MAX_HELP_TIME,
	DOCS_MAX_TIME_PERFILE_SECONDS,
} from '../../models/docs-check';
import { IDocsConf, IDocsErrorResponse, IDocsPhotoTakenEvent, IDocsResponse } from '../../models/documents-check';
import {
	DOCS_TYPE,
	CONNECTION_TYPE,
	DocsLog,
	IDocumentsDocs,
	IDocumentsOperation,
	IFinishOperation,
	IOperation,
	IPageWarning,
	LocalizedText,
	OPERATION_STATUS,
	OPERATION_TYPE,
	PAGE_WARNING,
	PROCESS_STATUS,
	TestStatus,
	TEST_STATUS,
} from '../../models/user-identification';
import { DocsService } from '../../services/docs/docs.service';
import { DocsTimerManagerService } from '../../services/docs/timer-manager.service';
import { ProcessHelperService } from '../../services/process-helper.service';
import { ProcessService } from '../../services/process.service';
import { DocumentUploadComponent } from './components/document-upload/document-upload.component';
import { DocumentActionsComponent } from './components/document-actions/document-actions.component';
import { DocsTakePhotoComponent } from './docs-take-photo/docs-take-photo.component';
import { ModalInstructionsComponent } from './modals/modal-instructions.component';
import { ModalPendingDocumentsComponent } from './modals/modal-pending-documents.component';
import { ReactiveFormsService } from 'src/app/core/helpers/reactive-forms.service';
import { ModalIrpfCveHelpComponent } from 'src/app/shared/components/modal-irpf-cve-help/modal-irpf-cve-help.component';
import { PwcsInlineSvgComponent } from 'src/app/shared/components/pwcs-inline-svg/pwcs-inline-svg.component';
import { ConnectionsService } from '../../services/connections/connections.service';
import { KoDebugImagesService } from '../../services/debug/ko-debug-images.service';

@Component({
	selector: 'app-docs',
	imports: [
		CommonModule,
		ReactiveFormsModule,
		TranslatePipe,
		AlertComponent,
		ButtonComponent,
		ButtonIconComponent,
		IconComponent,
		LinkComponent,
		NavHelpComponent,
		ProgressComponent,
		PwcsLayoutComponent,
		SpinnerComponent,
		TagComponent,
		TileComponent,
		WatermarkComponent,
		ItemComponent,
		ItemGroupComponent,
		ListDetailComponent,
		DocumentUploadComponent,
		DocumentActionsComponent,
		DocsTakePhotoComponent,
		HandsetLandscapeTileComponent,
		ModalScanQrComponent,
		ModalChangeDeviceComponent,
		ModalInstructionsComponent,
		ModalPendingDocumentsComponent,
		DividerComponent,
		FormGroupComponent,
		InputComponent,
		ModalIrpfCveHelpComponent,
		PwcsInlineSvgComponent,
	],
	templateUrl: './docs.component.html',
	styleUrls: ['./docs.component.scss'],
})
export class DocsComponent implements OnInit, OnDestroy {
	reactiveFormService = inject(ReactiveFormsService);
	private languageService = inject(LanguageService);
	private koDebugImagesService = inject(KoDebugImagesService);
	@ViewChild(DocsTakePhotoComponent) private docsTakePhotoComponent?: DocsTakePhotoComponent;

	@Input() operations: IOperation[];
	@Input()
	get operation(): IDocumentsOperation {
		return this._operation;
	}
	set operation(operation: IDocumentsOperation) {
		this._operation = operation;
		this.applyLocalizedConfigText();
		this.showLoading(this.trans.instant('GENERAL.LOADING.INITIAL_SETUP'));
		this.totalDocCount = this._operation.config?.docs?.length || 0;
		// Actualizar subtítulo
		this.updateSubtitle();
		this.docsConf = this.setDocsSetting();
		this.initializeFormStructures();
		this.setupDocumentControls();
	}

	private _operation: IDocumentsOperation;

	// General
	public loading: ILoading = {
		show: false,
		text: '',
	};
	public DocsLog: typeof DocsLog = DocsLog;
	public LogType: typeof LogType = LogType;
	public openQR: boolean = false;
	public handsetLandscape: boolean = false;
	private destroy$: Subject<void> = new Subject<void>();
	private languagePickerUi = inject(LanguagePickerUiService);
	public pageWarning: IPageWarning = { show: false, type: null };
	public PAGE_WARNING = PAGE_WARNING;
	public navsHelp: Array<INavHelp> = this.navHelpService.getNavsHelp([NavHelpAction.VOLUMEN]);
	public navsHelpTutorial: Array<INavHelp> = this.navHelpService.getTutorialNavsHelp();
	public isHelpByNavHelp: boolean = false;

	// Operation
	private initOperation: boolean = false;

	// Textos y alertas
	public subtitle: string = '';
	public helpTitle: string = 'DOCUMENTS.HELP.TITLE';

	// Controles
	public docControls: { [uuid: string]: FormArray } = {};
	public selectedFiles: { [uuid: string]: (File | null)[] } = {};
	public testMode: boolean = false;
	public photoCaptureConfig: { doc: IDocumentsDocs; fileExtension?: string; faceSlot?: 0 | 1 };
	public isVisibleCapture: boolean = false;

	public formCVE: FormControl = new FormControl('', [Validators.minLength(16), Validators.maxLength(16)]);

	// Document check
	public docsConf: IDocsConf;
	public totalDocCount: number = 0;

	// Accordion control for multiple documents
	public expandedDocUuid: string | null = null;

	// Modals
	public isVisibleScan: boolean = false;
	public isVisibleChangeDevice: boolean = false;
	public isVisibleInstructions: boolean = false;
	public isVisiblePendingDocuments: boolean = false;
	public isVisibleCveIrpfHelp: boolean = false;
	public selectedDoc: IDocumentsDocs;

	// File size alert
	public fileSizeAlerts: { [docUuid: string]: { show: boolean; message: string } } = {};

	// File extension alert
	public fileExtensionAlerts: { [docUuid: string]: { show: boolean; message: string } } = {};

	// Too many files alert
	public tooManyFilesAlerts: { [docUuid: string]: { show: boolean; message: string } } = {};

	// Not all files added alert
	public notAllFilesAddedAlerts: { [docUuid: string]: { show: boolean; message: string } } = {};

	// Document status tracking
	public documentStatuses: { [docUuid: string]: 'OK' | 'KO' | null } = {};
	public documentServerErrors: { [docUuid: string]: boolean } = {};

	// Inactivity timer
	private inactivityTimer: Subscription | null = null;
	private operationTitleSource: LocalizedText | undefined;
	private localizedDocSource: Record<
		string,
		{
			title?: LocalizedText;
			description?: LocalizedText;
			instructions?: LocalizedText;
			instructionsPhoto?: LocalizedText;
		}
	> = {};

	// Help
	public isHelpAdditionalInfo: boolean = false;

	constructor(
		public progressService: ProgressService,
		public audioService: AudioService,
		public processHelperService: ProcessHelperService,
		public cloudLogsService: CloudLogsService,
		public designService: DesignService,
		public auth: AuthService,
		private loadingService: LoadingService,
		private route: ActivatedRoute,
		private trans: TranslateService,
		private configService: ConfigService,
		private processService: ProcessService,
		private breakpointObserver: BreakpointObserver,
		private navHelpService: NavHelpService,
		private masterService: MasterService,
		private docsService: DocsService,
		private timerManagerService: DocsTimerManagerService,
		private errorService: ErrorService,
		private identitySDKService: IdentitySDKService,
		private connectionsService: ConnectionsService,
		private cdr: ChangeDetectorRef,
	) {
		this.trans.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.updateSubtitle();
			this.cdr.detectChanges();
		});

		this.route.queryParams.subscribe((params: any) => {
			this.testMode = Boolean(params.testmode);
		});

		this.breakpointObserver
			.observe([Breakpoints.HandsetLandscape])
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				this.handsetLandscape = result.matches;
				if (this.handsetLandscape) {
					this.stopTotalTime();
					if (!this.pageWarning.show) this.startTotalTime();
				} else {
					this.startTotalTime();
					if (!this.pageWarning.show) this.stopTotalTime();
				}
			});

		this.formCVE.valueChanges.subscribe(() => {
			this.updateDocumentStatus(this.operation.config.docs.find((doc) => doc.type === 'IRPF')?.uuid);
		});
	}

	ngOnInit(): void {
		this.configService.config$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.applyLocalizedConfigText();
			this.updateSubtitle();
		});
		this.trans.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.applyLocalizedConfigText();
			this.updateSubtitle();
			this.refreshSelectedDocOnLanguageChange();
		});

		this.masterService
			.getMaster()
			.pipe(takeUntil(this.destroy$))
			.subscribe((master: IMaster) => {
				const connectionDoc = this.operation?.config?.docs?.find((doc) => doc.connection_type);
				if (master && !this.initOperation && !connectionDoc) {
					this.initOperation = true;
					this.processService.initOperation(this.operation.type);
					// Iniciar temporizador de tiempo máximo
					this.startMaxTimeTimer();
					// Actualizar estado del proceso
					this.processService.updateProcessStatus(PROCESS_STATUS.INPROGRESS).subscribe();
				}
			});

		// Cargar audios para el modo "Sacar foto"
		// this.audioService.preloadOcrMrzAudios();

		// Ocultar loading
		this.hideLoading();
	}

	ngOnDestroy(): void {
		this.stopTotalTime();
		this.stopInactivityTimer();
		this.stopMaxTimeTimer();
		// Limpiar alertas de tamaño de archivo
		this.fileSizeAlerts = {};
		this.fileExtensionAlerts = {};
		this.destroy$.next();
		this.destroy$.complete();
	}

	// #region Main Process
	/**
	 * Envía los documentos asociados a la operación actual.
	 * Si no existen documentos en la configuración de la operación, la función no realiza ninguna acción.
	 * Valida que todos los documentos requeridos cumplan con los requisitos mínimos antes de enviar.
	 * Muestra un indicador de carga mientras se realiza la validación de los documentos.
	 */
	public sendDocs(): void {
		// Si no hay documentos, no hacer nada
		if (!this.operation?.config?.docs || this.operation.config.docs.length === 0) return;

		// Validar que todos los documentos requeridos cumplan con los requisitos mínimos
		if (!this.areRequiredDocumentsValid()) {
			// Marcar como KO todos los documentos que no cumplan el mínimo exigido
			this.operation.config.docs.forEach((doc) => {
				if (!this.isDocumentValid(doc)) {
					if (!doc.required && this.getUploadedCount(doc) === 0) {
						delete this.documentStatuses[doc.uuid];
					} else {
						this.documentStatuses[doc.uuid] = 'KO';
					}
				}
			});
			this.isVisiblePendingDocuments = true;
			return;
		}

		this.loading = {
			show: true,
			text: this.trans.instant('GENERAL.LOADING.LIFE_CHECK_VALIDATION'),
		};
		this.sendResult();
	}

	/** NIF-991: motivo del último sendResult(false) para correlacionar con 2020 / orphan. */
	private lastDocsCancelReason: string | null = null;

	private async sendResult(valid: boolean = true, cancelReason?: string): Promise<void> {
		try {
			if (!valid) {
				// NIF-1094: no cancelar por max_time/help si ya hay upload/OCR en curso (loading).
				if (
					this.loading?.show &&
					(cancelReason === 'max_time' || cancelReason === 'help_timeout')
				) {
					this.cloudLogsService.sendCloudLogs(
						{
							cancelReason,
							skipped: 'upload_in_progress',
						},
						DocsLog.FINISH,
						LogType.Warning,
					);
					return;
				}
				this.lastDocsCancelReason = cancelReason ?? this.lastDocsCancelReason ?? 'unknown';
				this.cloudLogsService.sendCloudLogs(
					{
						cancelReason: this.lastDocsCancelReason,
						helpTimerActive: Boolean(this.inactivityTimer),
					},
					DocsLog.FINISH,
					LogType.Warning,
				);
				this.loading = {
					show: true,
					text: this.trans.instant('GENERAL.LOADING.LIFE_CHECK_VALIDATION'),
				};
				this.finishDocsProcess(OPERATION_STATUS.KO);
				return;
			}

			// NIF-1094: espejo OCR stopTimersDuringRequest — max_time no debe disparar 2020 durante OCR carnet.
			this.stopMaxTimeTimer();
			this.stopInactivityTimer();

			// Obtener documentos
			const documentsToSend = await this.getDocumentsToSend(valid);

			// Incrementar contadores de intentos antes de enviar
			this.incrementDocumentAttempts(documentsToSend);

			// Enviar cada documento individualmente usando Promise.all
			const sendPromises = documentsToSend.map((doc) => this.sendSingleDocumentResult(doc));
			const responses = await Promise.all(sendPromises);

			// Separar respuestas exitosas de errores
			const successfulResponses = responses.filter((response) => !response.isError) as (IDocsResponse & {
				docUuid: string;
			})[];
			const errorResponses = responses.filter((response) => response.isError) as {
				docUuid: string;
				isError: true;
				error: any;
			}[];

			// Actualizar estados de documentos basándose en las respuestas
			this.updateDocumentStatuses(successfulResponses, errorResponses);

			// Verificar que todas las respuestas de documentos requeridos sean exitosas
			const requiredDocumentResponses = successfulResponses.filter((response) => {
				const docConfig = this._operation.config?.docs?.find((doc) => doc.uuid === response.docUuid);
				return docConfig?.required === true;
			});

			// Verificar si hay errores en documentos requeridos
			const requiredErrorResponses = errorResponses.filter((response) => {
				const docConfig = this._operation.config?.docs?.find((doc) => doc.uuid === response.docUuid);
				return docConfig?.required === true;
			});

			const allRequiredSuccessful =
				requiredErrorResponses.length === 0 &&
				requiredDocumentResponses.every(
					(response) =>
						response.result === OPERATION_STATUS.OK ||
						response.result?.toUpperCase() === OPERATION_STATUS.OK,
				);

			if (allRequiredSuccessful) {
				this.finishDocsProcess(OPERATION_STATUS.OK);
			} else {
				// Filtrar solo documentos requeridos para verificar reintentos y errores
				const requiredDocumentsToSend = documentsToSend.filter((docPayload) => {
					const docConfig = this._operation.config?.docs?.find((doc) => doc.uuid === docPayload.docUuid);
					return docConfig?.required === true;
				});

				// Para verificar reintentos, necesitamos tanto respuestas exitosas como errores de documentos requeridos
				const requiredSuccessfulResponses = requiredDocumentResponses;
				const requiredFailedResponses = requiredErrorResponses;

				// Combinar todas las respuestas de documentos requeridos para verificar reintentos
				const allRequiredResponses = [
					...requiredSuccessfulResponses,
					...requiredFailedResponses.map((r) => r.error), // Usar el error que contiene la IDocsResponse
				];

				// Verificar si algún documento requerido agotó sus reintentos
				const hasExhaustedRetries = this.checkExhaustedRetries(requiredDocumentsToSend, allRequiredResponses);

				if (hasExhaustedRetries) {
					// NIF-990 / NIF-995: KO definitivo o 5xx interno — back ya cerró o no hay contrato; no op_status=KO (2020).
					const definitiveUploadKo = this.findDefinitiveUploadKoResponse(allRequiredResponses);
					if (definitiveUploadKo) {
						this.handleSendResultError(definitiveUploadKo);
					} else {
						this.sendResult(false, 'exhausted_retries');
					}
				} else {
					// Si hay reintentos disponibles, manejar como error normal para permitir nuevo intento
					// Usar el primer error de documento requerido o la primera respuesta fallida
					let errorToHandle: { error: IDocsErrorResponse };

					if (requiredFailedResponses.length > 0) {
						errorToHandle = { error: requiredFailedResponses[0].error as IDocsErrorResponse };
					} else {
						// Si no hay errores explícitos, buscar respuestas exitosas pero con resultado KO
						const failedSuccessfulResponses = requiredSuccessfulResponses.filter(
							(response) =>
								response.result !== OPERATION_STATUS.OK &&
								response.result?.toUpperCase() !== OPERATION_STATUS.OK,
						);
						errorToHandle = { error: failedSuccessfulResponses[0] as unknown as IDocsErrorResponse };
					}

					this.handleSendResultError(errorToHandle);
				}
			}
		} catch (error) {
			this.clearDocsLoading();
			this.handleSendResultError(error);
		}
	}

	/**
	 * Finaliza el proceso de documentos enviando el estado de la operación al backend.
	 *
	 * @param status - El estado de la operación a enviar (OPERATION_STATUS).
	 */
	private finishDocsProcess(status: OPERATION_STATUS): void {
		let request;
		const manualConnection = this.operation?.config?.docs.find((doc) => doc.connection_type);
		if (manualConnection) {
			if (status === OPERATION_STATUS.OK) {
				this.finishDocsProcessSuccess({ result: OPERATION_STATUS.OK }, status);
				this.clearDocsLoading();
				return;
			}
			if (manualConnection.connection_type === CONNECTION_TYPE.IRPF) {
				request = this.connectionsService.irpfFinish(manualConnection.uuid);
			}
			if (manualConnection.connection_type === CONNECTION_TYPE.WORK_LIFE) {
				request = this.connectionsService.workLifeFinish(manualConnection.uuid);
			}
			if (manualConnection.connection_type === CONNECTION_TYPE.SCRIPTURE) {
				request = this.connectionsService.scriptureFinish(manualConnection.uuid);
			}
		} else {
			const formData = new FormData();
			formData.append('op_status', status);
			formData.append('device_id', this.auth.getDeviceId());
			request = this.docsService.sendDocsResultFormData(formData);
		}

		if (!request) {
			this.verificationFail(OPERATION_STATUS.KO);
			this.clearDocsLoading();
			return;
		}

		// Notificar al backend que se ha completado el proceso de documentos
		request.pipe(finalize(() => this.clearDocsLoading())).subscribe({
			next: (response) => {
				this.finishDocsProcessSuccess(response, status);
			},
			error: (error) => {
				this.cloudLogsService.sendCloudLogs(
					{
						opStatus: status,
						cancelReason: this.lastDocsCancelReason,
						error,
					},
					DocsLog.FINISH,
					LogType.Error,
				);
				this.verificationFail(OPERATION_STATUS.KO);
			},
		});
	}

	/** Quita el spinner local de docs (no el LoadingService global). */
	private clearDocsLoading(): void {
		this.loading.show = false;
	}

	private finishDocsProcessSuccess(response: any, status: OPERATION_STATUS): void {
		if (response.result === OPERATION_STATUS.OK) {
			// Notificar al SDK el éxito de la operación de documentos
			this.identitySDKService.notifyOperationSuccess(OperationType.DOCUMENTS, {
				result: response.result,
				response: response,
				status: status,
			});

			this.nextOperation(status);
		} else {
			// Notificar al SDK el error de la operación de documentos
			this.identitySDKService.notifyOperationError(OperationType.DOCUMENTS, 'Documents operation failed', {
				result: response.result,
				status: status,
			});

			this.cloudLogsService.sendCloudLogs({ error: response }, DocsLog.FINISH, LogType.Error);
			this.verificationFail(OPERATION_STATUS.KO);
		}
	}

	/**
	 * Obtiene los documentos con archivos válidos para enviar al servicio.
	 *
	 * @param valid Indica si la validación fue exitosa.
	 * @returns Promise que resuelve con array de documentos para enviar.
	 */
	private async getDocumentsToSend(
		valid: boolean,
	): Promise<Array<{ formData: FormData; docUuid: string; isCve?: boolean; connection_type?: CONNECTION_TYPE }>> {
		const documentsToSend: Array<{
			formData: FormData;
			docUuid: string;
			isCve?: boolean;
			connection_type?: CONNECTION_TYPE;
		}> = [];

		try {
			// Procesar todos los documentos con archivos
			this._operation.config?.docs?.forEach((docConfig: IDocumentsDocs) => {
				// IRPF tiene valir en el form
				if (docConfig.type === 'IRPF' && this.formCVE.value) {
					const formData = new FormData();
					formData.append('csv', this.formCVE.value?.toUpperCase());
					formData.append('uuid_config', docConfig.uuid);
					documentsToSend.push({
						formData,
						docUuid: docConfig.uuid,
						isCve: true,
						connection_type: docConfig.connection_type,
					});
				}

				const selectedFiles = this.selectedFiles[docConfig.uuid];
				if (!selectedFiles) return;

				// Filtrar archivos no nulos
				const validFiles = selectedFiles.filter((file) => file !== null) as File[];
				if (validFiles.length === 0 && valid) return;

				// Crear FormData para este documento
				const formData = new FormData();
				if (docConfig.connection_type) {
					formData.append('uuid_config', docConfig.uuid);
				} else {
					formData.append('document_type_uuid', docConfig.document_type_uuid);
					formData.append('uuid', docConfig.uuid);
					formData.append('device_id', this.auth.getDeviceId());
					formData.append('status', valid.toString());
				}
				// Agregar todos los archivos como array
				validFiles.forEach((file) => {
					formData.append('files[]', file, file.name);
				});

				documentsToSend.push({
					formData,
					docUuid: docConfig.uuid,
					connection_type: docConfig.connection_type,
				});
			});
		} catch (error) {
			this.cloudLogsService.sendCloudLogs(
				{ observations: `Error preparing FormData: ${error.message || error}` },
				DocsLog.IN_PROCESS,
				LogType.Error,
			);
			this.clearDocsLoading(); // Detener loading
			this.setPageWarning(PAGE_WARNING.SERVER_ERROR);
			throw error; // Re-throw para que el llamador maneje el error
		}

		return documentsToSend;
	}

	/**
	 * Incrementa el contador de intentos para los documentos que van a ser enviados.
	 *
	 * @param documentsToSend Array de documentos que van a ser enviados.
	 */
	private incrementDocumentAttempts(
		documentsToSend: Array<{ formData: FormData; docUuid: string; isCve?: boolean }>,
	): void {
		documentsToSend.forEach((documentPayload) => {
			const docConfig = this.docsConf.documents.find((doc) => doc.uuid === documentPayload.docUuid);
			if (docConfig) docConfig.currentTries++;
		});
	}

	/**
	 * Verifica si alguno de los documentos a enviar o alguna de las respuestas ha agotado el número máximo de reintentos permitidos.
	 *
	 * @param documentsToSend - Array de objetos que contienen la información de los documentos a enviar, incluyendo el FormData y el UUID del documento.
	 * @param responses - Array de respuestas recibidas, cada una representando el estado de reintentos de un documento.
	 * @returns `true` si al menos un documento o respuesta ha agotado los reintentos; `false` en caso contrario.
	 */
	private checkExhaustedRetries(
		documentsToSend: Array<{ formData: FormData; docUuid: string; isCve?: boolean }>,
		responses: (IDocsResponse | IDocsErrorResponse | any)[],
	): boolean {
		const hasDocsExhaustedRetries = documentsToSend.some((documentPayload) => {
			const docConfig = this.docsConf.documents.find((doc) => doc.uuid === documentPayload.docUuid);
			if (!docConfig) return true; // Si no se encuentra config, considerarlo agotado

			const exhausted = docConfig.currentTries >= docConfig.total_tries;

			return exhausted;
		});

		const hasResponsesExhaustedRetries = responses.some((response) => {
			const resp = 'error' in response ? response.error : response;
			return (
				!resp.retry && resp.result !== OPERATION_STATUS.OK && resp.result?.toUpperCase() !== OPERATION_STATUS.OK
			);
		});

		return hasDocsExhaustedRetries || hasResponsesExhaustedRetries;
	}

	/**
	 * Obtiene información del estado de reintentos de un documento específico.
	 *
	 * @param docUuid UUID del documento.
	 * @returns Información del estado de reintentos.
	 */
	public getDocumentRetryInfo(docUuid: string): { currentTries: number; totalTries: number; canRetry: boolean } {
		const docConfig = this.docsConf.documents.find((doc) => doc.uuid === docUuid);

		if (!docConfig) return { currentTries: 0, totalTries: 0, canRetry: false };

		return {
			currentTries: docConfig.currentTries,
			totalTries: docConfig.total_tries,
			canRetry: docConfig.currentTries < docConfig.total_tries,
		};
	}

	/**
	 * Envía un documento individual al servicio usando FormData.
	 *
	 * @param documentPayload Payload del documento individual con FormData e información del documento.
	 * @returns Promise que resuelve con la respuesta del servicio o información del error.
	 */
	private sendSingleDocumentResult(documentPayload: {
		formData: FormData;
		docUuid: string;
		isCve?: boolean;
		connection_type?: CONNECTION_TYPE;
	}): Promise<
		(IDocsResponse & { docUuid: string; isError?: false }) | { docUuid: string; isError: true; error: any }
	> {
		if (documentPayload.isCve) {
			return new Promise((resolve) => {
				this.docsService.sendIrpfCve(documentPayload.formData).subscribe({
					next: (response) => {
						resolve({ ...response, docUuid: documentPayload.docUuid, isError: false });
					},
					error: (error) => {
						resolve({ docUuid: documentPayload.docUuid, isError: true, error });
					},
				});
			});
		}
		if (documentPayload.connection_type) {
			return new Promise((resolve) => {
				let request;
				if (documentPayload.connection_type === CONNECTION_TYPE.IRPF) {
					request = this.connectionsService.irpfManual(documentPayload.formData);
				}
				if (documentPayload.connection_type === CONNECTION_TYPE.WORK_LIFE) {
					request = this.connectionsService.workLifeManual(documentPayload.formData);
				}
				if (documentPayload.connection_type === CONNECTION_TYPE.SCRIPTURE) {
					request = this.connectionsService.scriptureManual(documentPayload.formData);
				}
				request.subscribe({
					next: (response) => {
						resolve({ ...response, docUuid: documentPayload.docUuid, isError: false });
					},
					error: (error) => {
						resolve({ docUuid: documentPayload.docUuid, isError: true, error });
					},
				});
			});
		}
		return new Promise((resolve) => {
			this.docsService.sendDocsResultFormData(documentPayload.formData).subscribe({
				next: (response) => {
					resolve({ ...response, docUuid: documentPayload.docUuid, isError: false });
				},
				error: (error) => {
					resolve({ docUuid: documentPayload.docUuid, isError: true, error });
				},
			});
		});
	}

	/**
	 * Maneja el error ocurrido al enviar el resultado de un documento.
	 *
	 * Establece el indicador de carga a falso, captura el nombre del documento que falló,
	 * y muestra una advertencia de error de servidor en la página.
	 */
	private handleSendResultError(error: { error: IDocsErrorResponse } | unknown): void {
		// Log del error
		this.cloudLogsService.sendCloudLogs({ error }, DocsLog.IN_PROCESS, LogType.Error);

		const parsed = this.extractDocsOperationError(error);

		// Error general (10xx)
		if (
			parsed.errorCode?.startsWith('10') &&
			this.errorService.hasGeneralError({
				code: ERROR_STATUS.DATA,
				error: { error_code: parsed.errorCode as ERROR_CODE, msg: '' },
			})
		) {
			this.clearDocsLoading();
			return;
		}

		// Error controlado
		if (parsed.errorCode) {
			const errorCode = parsed.errorCode as ERROR_CODE;
			this.handleInternalCodeError(errorCode);

			if (parsed.result === OPERATION_STATUS.KO && !parsed.retry) {
				// NIF-990/NIF-995: copy Finish (catálogo/i18n). NIF-1026: si finish devuelve ko_reason, prevalece.
				this.applyFinishKoCopyFromDocsError(parsed);
				this.verificationFail(OPERATION_STATUS.KO);
				return;
			}

			const expiredI18n = this.resolveExpiredDocumentDocsErrorI18n(errorCode);
			if (expiredI18n) {
				this.showError(expiredI18n.titleKey, expiredI18n.subtitleKey);
				return;
			}

			const internalExtractI18n = this.resolveInternalExtractDocsErrorI18n(errorCode);
			if (internalExtractI18n) {
				this.showError(internalExtractI18n.titleKey, internalExtractI18n.subtitleKey);
				return;
			}

			const scriptureI18n = this.resolveScriptureDocsErrorI18n(errorCode, parsed.businessCode);
			this.showError(scriptureI18n?.titleKey, scriptureI18n?.subtitleKey);
		} else if (this.isHttpServerErrorResponse(error)) {
			// NIF-995: 5xx sin error_code — UX 2048, no finish 2020.
			this.applyFinishKoCopyFromDocsError({
				errorCode: ERROR_CODE.DOCUMENTS_INTERNAL_EXTRACT,
			});
			this.verificationFail(OPERATION_STATUS.KO);
		} else {
			// Error no controlado
			this.showError();
		}
	}

	/**
	 * NIF-990 / NIF-995: upload ya cerrado (`retry: false`) o HTTP 5xx — no llamar finishDocsProcess.
	 */
	public findDefinitiveUploadKoResponse(
		responses: unknown[],
	): { error: IDocsErrorResponse } | null {
		for (const response of responses) {
			const parsed = this.extractDocsOperationError(response);
			if (parsed.result === OPERATION_STATUS.KO && parsed.retry === false) {
				return { error: response as IDocsErrorResponse };
			}
			// NIF-995: 500 interno sin contrato KO → no sendResult(false) → 2020.
			if (this.isHttpServerErrorResponse(response) || this.isHttpServerErrorResponse({ error: response })) {
				return {
					error: {
						...(typeof response === 'object' && response !== null ? response : {}),
						result: OPERATION_STATUS.KO,
						retry: false,
						error: {
							error_code: ERROR_CODE.DOCUMENTS_INTERNAL_EXTRACT,
							msg: 'Could not extract document information',
						},
					} as IDocsErrorResponse,
				};
			}
		}
		return null;
	}

	/** NIF-995: HttpErrorResponse / status >= 500 en capas de error de docs. */
	public isHttpServerErrorResponse(error: unknown): boolean {
		const candidates: unknown[] = [error];
		if (error && typeof error === 'object' && 'error' in error) {
			candidates.push((error as { error: unknown }).error);
		}
		for (const candidate of candidates) {
			if (!candidate || typeof candidate !== 'object') {
				continue;
			}
			const status = (candidate as { status?: number }).status;
			if (typeof status === 'number' && status >= 500) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Normaliza el cuerpo KO de docs tanto si llega como body, HttpErrorResponse
	 * o `{ error: HttpErrorResponse }` (ruta sendDocs).
	 */
	public extractDocsOperationError(error: unknown): {
		errorCode?: string;
		businessCode?: string;
		result?: string;
		retry: boolean;
		title?: string;
		message?: string;
	} {
		const root = error as {
			error?: unknown;
			result?: string;
			retry?: boolean;
			error_code?: string | number;
			business_error_code?: string | number;
			status?: number;
		} | null;

		const layers: unknown[] = [];
		if (root) layers.push(root);
		if (root && typeof root === 'object' && 'error' in root && root.error) layers.push(root.error);
		const nested = (root as { error?: { error?: unknown } } | null)?.error;
		if (nested && typeof nested === 'object' && 'error' in nested && (nested as { error?: unknown }).error) {
			layers.push((nested as { error: unknown }).error);
		}

		for (const layer of layers) {
			const candidate = layer as {
				error?: {
					error_code?: string | number;
					business_error_code?: string | number;
					msg?: string;
					title?: string;
					message?: string;
				};
				result?: string;
				retry?: boolean;
				error_code?: string | number;
				business_error_code?: string | number;
				title?: string;
				message?: string;
			};

			if (candidate?.error && (candidate.error.error_code != null || candidate.error.business_error_code != null)) {
				const parsed = {
					errorCode: candidate.error.error_code?.toString(),
					businessCode: candidate.error.business_error_code?.toString(),
					result: candidate.result,
					retry: candidate.retry ?? false,
				} as {
					errorCode?: string;
					businessCode?: string;
					result?: string;
					retry: boolean;
					title?: string;
					message?: string;
				};
				if (typeof candidate.error.title === 'string') {
					parsed.title = candidate.error.title;
				}
				if (typeof candidate.error.message === 'string') {
					parsed.message = candidate.error.message;
				}
				return parsed;
			}

			if (candidate?.error_code != null || candidate?.business_error_code != null) {
				const parsed = {
					errorCode: candidate.error_code?.toString(),
					businessCode: candidate.business_error_code?.toString(),
					result: root?.result ?? (root as { error?: { result?: string } })?.error?.result,
					retry:
						root?.retry ??
						(root as { error?: { retry?: boolean } })?.error?.retry ??
						false,
				} as {
					errorCode?: string;
					businessCode?: string;
					result?: string;
					retry: boolean;
					title?: string;
					message?: string;
				};
				if (typeof candidate.title === 'string') {
					parsed.title = candidate.title;
				}
				if (typeof candidate.message === 'string') {
					parsed.message = candidate.message;
				}
				return parsed;
			}
		}

		return { retry: false };
	}

	/** NIF-990: i18n documento caducado en paso DOCUMENTS. */
	public resolveExpiredDocumentDocsErrorI18n(
		errorCode?: string | null,
	): { titleKey: string; subtitleKey: string } | null {
		if (errorCode?.toString() !== ERROR_CODE.FRAUD_SCORING_EXPIRED_DOCUMENT) {
			return null;
		}
		return {
			titleKey: 'DOCUMENTS.ERROR.EXPIRED_DOCUMENT.TITLE',
			subtitleKey: 'DOCUMENTS.ERROR.EXPIRED_DOCUMENT.DESCRIPTION',
		};
	}

	/** NIF-995: i18n fallo interno / extracción DOCUMENTS. */
	public resolveInternalExtractDocsErrorI18n(
		errorCode?: string | null,
	): { titleKey: string; subtitleKey: string } | null {
		if (errorCode?.toString() !== ERROR_CODE.DOCUMENTS_INTERNAL_EXTRACT) {
			return null;
		}
		return {
			titleKey: 'DOCUMENTS.ERROR.INTERNAL_EXTRACT.TITLE',
			subtitleKey: 'DOCUMENTS.ERROR.INTERNAL_EXTRACT.DESCRIPTION',
		};
	}

	/**
	 * NIF-990 / NIF-995: copia FINISH desde catálogo API o i18n (caducidad / extract interno).
	 * NIF-1026: si `POST /operation/finish` trae `ko_reason`, Finish lo prioriza sobre este patch.
	 */
	private applyFinishKoCopyFromDocsError(parsed: {
		errorCode?: string;
		title?: string;
		message?: string;
	}): void {
		const finishKeys =
			this.resolveExpiredDocumentDocsErrorI18n(parsed.errorCode) ||
			this.resolveInternalExtractDocsErrorI18n(parsed.errorCode);
		const title =
			parsed.title ||
			(finishKeys ? this.trans.instant(finishKeys.titleKey) : null);
		const description =
			parsed.message ||
			(finishKeys ? this.trans.instant(finishKeys.subtitleKey) : null);
		if (!title && !description) {
			return;
		}
		const foundFinish = this.operations.find(
			(op: IOperation) => op.type === OPERATION_TYPE.FINISH,
		) as IFinishOperation | undefined;
		if (!foundFinish) {
			return;
		}
		if (title) {
			foundFinish.config.ko_title = title as LocalizedText;
		}
		if (description) {
			foundFinish.config.ko_description = description as LocalizedText;
		}
	}

	/**
	 * NIF-831: tipifica KO recuperable de SCRIPTURE PDF (2053/2056/2057 o wrap 2024 + business_error_code).
	 */
	public resolveScriptureDocsErrorI18n(
		errorCode?: string | null,
		businessCode?: string | null,
	): { titleKey: string; subtitleKey: string } | null {
		const code = (businessCode || errorCode)?.toString();
		switch (code) {
			case ERROR_CODE.SCRIPTURE_PERSON_NOT_MATCHED:
				return {
					titleKey: 'DOCUMENTS.ERROR.SCRIPTURE.PERSON_NOT_MATCHED_TITLE',
					subtitleKey: 'DOCUMENTS.ERROR.SCRIPTURE.PERSON_NOT_MATCHED_DESCRIPTION',
				};
			case ERROR_CODE.SCRIPTURE_CIF_NOT_MATCHED:
				return {
					titleKey: 'DOCUMENTS.ERROR.SCRIPTURE.CIF_NOT_MATCHED_TITLE',
					subtitleKey: 'DOCUMENTS.ERROR.SCRIPTURE.CIF_NOT_MATCHED_DESCRIPTION',
				};
			case ERROR_CODE.SCRIPTURE_INVALID:
				return {
					titleKey: 'DOCUMENTS.ERROR.SCRIPTURE.INVALID_TITLE',
					subtitleKey: 'DOCUMENTS.ERROR.SCRIPTURE.INVALID_DESCRIPTION',
				};
			default:
				return null;
		}
	}

	private showError(titleKey?: string, subtitleKey?: string): void {
		this.cloudLogsService.sendCloudLogs(
			{
				titleKey: titleKey ?? null,
				subtitleKey: subtitleKey ?? null,
				cancelReason: this.lastDocsCancelReason,
			},
			DocsLog.IN_PROCESS,
			LogType.Warning,
		);
		this.clearDocsLoading();
		// NIF-1094: tras KO recuperable, rearmar max_time (se pausó al enviar).
		this.startMaxTimeTimer();
		this.pageWarning = {
			show: true,
			type: PAGE_WARNING.SERVER_ERROR,
			...(titleKey ? { titleKey } : {}),
			...(subtitleKey ? { subtitleKey } : {}),
		};
		this.updateSubtitle();
	}

	/**
	 * Maneja los errores internos relacionados con códigos específicos de error.
	 *
	 * @param errorCode El código de error de tipo `ERROR_CODE` que se ha producido.
	 *
	 * Si el código de error corresponde a códigos específicos de documentos, se puede manejar la lógica correspondiente.
	 */
	private handleInternalCodeError(errorCode: ERROR_CODE): void {
		switch (errorCode) {
			case ERROR_CODE.PERMISSION_NOT_GRANTED:
				this.verificationFail(OPERATION_STATUS.KO);
				break;
			case ERROR_CODE.FRAUD_SCORING_EXPIRED_DOCUMENT:
				// NIF-990: copy/FINISH en handleSendResultError (no tile SERVER_ERROR).
				break;
			case ERROR_CODE.DOCUMENTS_INTERNAL_EXTRACT:
				// NIF-995: copy/FINISH en handleSendResultError (no tile SERVER_ERROR).
				break;
			case ERROR_CODE.NOT_BE_READ:
				this.setPageWarning(PAGE_WARNING.SERVER_ERROR);
				break;
			case ERROR_CODE.INCORRECT_NUMBER_OF_DOCUMENTS:
				this.setPageWarning(PAGE_WARNING.SERVER_ERROR);
				break;
			default:
				// Error no específicamente manejado
				this.setPageWarning(PAGE_WARNING.SERVER_ERROR);
				break;
		}
	}

	/**
	 * KO por permisos (`ko-permission` en `data`) tras timeout en captura con cámara (sin archivo).
	 */
	private async handlePhotoCapturePermissionKo(docUuid: string, debugImage?: string): Promise<void> {
		this.exitPhotoCapture();

		const docConfig = this._operation.config?.docs?.find((doc) => doc.uuid === docUuid);
		if (!docConfig) {
			this.verificationFail(OPERATION_STATUS.KO);
			return;
		}

		this.loading = {
			show: true,
			text: this.trans.instant('GENERAL.LOADING.LIFE_CHECK_VALIDATION'),
		};

		this.koDebugImagesService.sendDocsKoDebugLogs(
			false,
			TEST_STATUS.KO_PERMISSION,
			'documents-permission-ko',
			[debugImage],
			{
				operationType: this.operation?.type,
				docUuid,
			},
		);

		const formData = new FormData();
		formData.append(
			'data',
			JSON.stringify([
				{
					docsConf: {
						...this.docsConf,
						general: {
							...this.docsConf.general,
							finishAt: new Date().toISOString(),
						},
					},
				},
				{ status: TEST_STATUS.KO_PERMISSION },
			]),
		);
		if (docConfig.connection_type) {
			formData.append('uuid_config', docConfig.uuid);
			formData.append('status', 'false');
		} else {
			formData.append('document_type_uuid', docConfig.document_type_uuid);
			formData.append('uuid', docConfig.uuid);
			formData.append('device_id', this.auth.getDeviceId());
			formData.append('status', 'false');
		}

		try {
			const response = await this.sendSingleDocumentResult({
				formData,
				docUuid,
				connection_type: docConfig.connection_type,
			});

			if ('isError' in response && response.isError) {
				this.handleSendResultError(response.error);
				return;
			}

			this.documentStatuses[docUuid] = 'KO';
			this.verificationFail(OPERATION_STATUS.KO);
		} catch {
			this.setPageWarning(PAGE_WARNING.SERVER_ERROR);
		} finally {
			this.loading.show = false;
		}
	}

	/**
	 * Maneja el fallo de verificación de documentos y finaliza el proceso.
	 *
	 * @param status El estado de la operación que indica el tipo de fallo.
	 */
	private verificationFail(status: OPERATION_STATUS): void {
		const foundFinish = this.operations.find((op: IOperation) => op.type === OPERATION_TYPE.FINISH);
		this.cloudLogsService.sendCloudLogs(
			{
				status,
				hasFinishOp: Boolean(foundFinish),
				cancelReason: this.lastDocsCancelReason,
			},
			DocsLog.FINISH,
			LogType.Warning,
		);
		this.clearDocsLoading();
		this.operation.completed = true;
		this.processService.unSubcribeCheckStatus();
		this.operations.forEach((op: IOperation) => {
			if (op.type !== OPERATION_TYPE.FINISH) {
				op.completed = true;
			}
		});
		if (foundFinish) {
			(foundFinish as IFinishOperation).config.status = status;
		} else {
			this.setPageWarning(PAGE_WARNING.VERIFICATION_FAIL);
		}
	}

	/**
	 * Marca la operación actual como completada y actualiza el estado de la operación de tipo FINISH.
	 *
	 * @param status El nuevo estado de la operación, de tipo OPERATION_STATUS.
	 */
	private nextOperation(status: OPERATION_STATUS): void {
		this.clearDocsLoading();
		this.operation.completed = true;
		const found = this.operations.find((s: IOperation) => s.type === OPERATION_TYPE.FINISH);
		if (found) {
			(found as IFinishOperation).config.status = status;
		}
	}

	/**
	 * Reinicia el proceso de identificación de usuario, incluyendo el tiempo total
	 * y las advertencias de página. Además, restablece los archivos
	 * seleccionados y los controles de documentos para permitir un nuevo intento.
	 *
	 * @param observations Observaciones asociadas al reinicio del proceso.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public retryAllProcess(_observations: string): void {
		this.startTotalTime();
		this.setPageWarning(null, false);
		// Cerrar alert de tamaño de archivo si está abierto
		this.closeAlert();
		// Detener el temporizador de inactividad al iniciar un nuevo intento
		this.stopInactivityTimer();

		// Resetear contadores de reintentos de todos los documentos menos cuando estamos en modo manual de una conexión
		const connectionDocs = this.operation?.config?.docs?.find((doc) => doc.connection_type);
		if (connectionDocs) return;
		this.docsConf.documents.forEach((docConfig) => {
			docConfig.currentTries = 0;
		});
	}
	// #endregion

	// #region Setup
	/**
	 * Inicializa las estructuras de formulario y archivos seleccionados basándose en la configuración de documentos.
	 */
	private initializeFormStructures(): void {
		this.selectedFiles = {};
		this.docControls = {};

		// Ordenar documentos. Primero los required
		this._operation.config?.docs?.sort((a, b) => {
			if (a.required && !b.required) return -1;
			if (!a.required && b.required) return 1;
			return 0;
		});

		// Expandir el primer documento por defecto
		if (this._operation.config?.docs && this._operation.config.docs.length > 0) {
			this.expandedDocUuid = this._operation.config.docs[0].uuid;
		}

		// Inicializar selectedFiles
		this._operation.config?.docs?.forEach((doc) => {
			const maxInputs = doc.max ?? doc.min ?? 1;
			this.selectedFiles[doc.uuid] = new Array(maxInputs).fill(null);
		});
	}

	/**
	 * Configura los controles de formulario agrupados por UUID de documento.
	 * Cada documento puede tener múltiples controles basados en su valor 'max'.
	 * Aplica validaciones basadas en 'min' y 'required' de cada documento.
	 */
	private setupDocumentControls(): void {
		this._operation.config?.docs?.forEach((doc) => {
			const maxInputs = doc.max ?? 1;
			const minInputs = doc.min ?? (doc.required ? 1 : 0);

			const formControls: FormControl[] = [];
			for (let i = 0; i < maxInputs; i++) {
				// Aplicar validación required solo a los primeros 'min' controles
				const validators = i < minInputs ? [Validators.required] : [];
				formControls.push(new FormControl(null, validators));
			}

			this.docControls[doc.uuid] = new FormArray(formControls);
		});
	}

	/**
	 * Inicializa y devuelve la configuración de documentos (`IDocsConf`) para el proceso de identificación de usuario.
	 *
	 * - Establece los valores generales como la fecha de inicio (`startAt`), estado inicial (`status`), y marca como incompleto.
	 * - Para cada documento en la configuración de la operación, asigna un identificador único (`uuid`), el número de reintentos permitidos (por defecto 3), el contador de intentos actuales y el estado inicial.
	 *
	 * @returns {IDocsConf} Configuración inicial de documentos para el proceso de identificación.
	 */
	private setDocsSetting(): IDocsConf {
		return {
			general: {
				startAt: new Date().toISOString(),
				finishAt: null,
				status: TestStatus.OnInit,
				complete: false,
			},
			documents:
				this._operation.config?.docs?.map((doc) => ({
					uuid: doc.uuid,
					// NIF-993: valor de perfil (todos los tipos); fallback 3 solo si el back no envía total_tries.
					total_tries: doc.total_tries ?? 3,
					currentTries: 0,
					status: TestStatus.OnInit,
				})) || [],
		};
	}
	// #endregion

	// #region Handle Documents
	/**
	 * Maneja la entrada de archivos seleccionados por el usuario y los asigna al documento y posición correspondientes.
	 *
	 * @param files Lista de archivos seleccionados por el usuario. Puede ser `null` si no se seleccionó ningún archivo.
	 * @param docUuid UUID del documento al que se asignará el archivo.
	 * @param index Índice de la posición dentro del array de archivos del documento. Por defecto es 0.
	 */
	public handleFileInput(files: FileList | null, docUuid: string, index: number = 0): void {
		if (!files || files.length === 0) return;

		// Cerrar cualquier alerta previa para este documento
		this.closeAlert(docUuid);

		// Obtener el documento para conocer sus límites
		const docConfig = this._operation.config?.docs?.find((doc) => doc.uuid === docUuid);
		if (!docConfig) return;

		const maxFiles = this.getMaxCount(docConfig);
		const currentFiles = this.getUploadedCount(docConfig);

		// Calcular cuántos archivos se pueden agregar sin exceder el máximo
		const availableSlots = maxFiles - currentFiles;
		const filesToProcess = Math.min(files.length, availableSlots);

		// Si no hay slots disponibles, mostrar error y salir
		if (availableSlots <= 0) {
			this.showMaxFilesError(docUuid, maxFiles);
			return;
		}

		// Procesar cada archivo seleccionado (validando individualmente)
		let filesProcessed = 0;
		let hasValidationErrors = false;

		for (let i = 0; i < filesToProcess; i++) {
			const selectedFile = files.item(i);
			if (!selectedFile) continue;

			// Encontrar el siguiente índice disponible
			const nextAvailableIndex = this.getNextAvailableIndex(docConfig);
			if (nextAvailableIndex === -1) break; // No hay más slots disponibles

			// Validar extensión del archivo (prioridad alta)
			if (!this.isFileExtensionValid(selectedFile)) {
				if (!hasValidationErrors) this.showFileExtensionError(docUuid);
				hasValidationErrors = true;
				continue;
			}

			// Validar que la extensión coincida con archivos existentes en el mismo documento (prioridad alta)
			if (!this.isExtensionConsistentWithDocument(selectedFile, docUuid)) {
				if (!hasValidationErrors) {
					this.showExtensionConsistencyError(docUuid, this.getDocumentRequiredExtension(docUuid));
				}
				hasValidationErrors = true;
				continue;
			}

			// Validar tamaño individual del archivo (prioridad alta)
			if (this.wouldExceedSizeLimit(docUuid, selectedFile)) {
				if (!hasValidationErrors) this.showFileSizeError(docUuid);
				hasValidationErrors = true;
				continue;
			}

			// Si pasa todas las validaciones, asignar el archivo
			if (this.selectedFiles[docUuid] && nextAvailableIndex < this.selectedFiles[docUuid].length) {
				this.selectedFiles[docUuid][nextAvailableIndex] = selectedFile;
			}

			if (this.docControls[docUuid] && this.docControls[docUuid].at(nextAvailableIndex)) {
				this.docControls[docUuid].at(nextAvailableIndex).setValue(selectedFile);
			}

			filesProcessed++;
		}

		// Solo mostrar alertas de cantidad si no hubo errores de validación
		if (!hasValidationErrors) {
			// Mostrar alerta si se seleccionaron más archivos de los disponibles o si no todos los archivos seleccionados se pudieron añadir
			if (files.length > availableSlots || filesProcessed < files.length) {
				this.showNotAllFilesAddedError(docUuid, files.length, filesProcessed);
			}
		}

		// Actualizar el control del formulario
		const control = this.getDocControl(docUuid, index);
		if (control) control.setValue(this.selectedFiles[docUuid].filter((file) => file !== null));

		// Actualizar el status del documento
		this.updateDocumentStatus(docUuid);
	}

	/**
	 * Elimina el archivo seleccionado en el índice especificado dentro del array de controles agrupados por uuid.
	 *
	 * @param docUuid UUID del documento.
	 * @param index Índice del archivo a eliminar dentro del array del documento.
	 */
	public removeFileByUuid(docUuid: string, index: number): void {
		if (this.docControls[docUuid] && this.docControls[docUuid].at(index)) {
			this.docControls[docUuid].at(index).setValue(null);
			if (Array.isArray(this.selectedFiles[docUuid])) {
				this.selectedFiles[docUuid][index] = null;
			}

			// Cerrar alertas para este documento al eliminar un archivo
			this.closeAlert(docUuid);

			// Actualizar el status del documento
			this.updateDocumentStatus(docUuid);
		}
	}

	/**
	 * Actualiza el status del documento basado en la cantidad de archivos actuales.
	 *
	 * @param docUuid UUID del documento a actualizar.
	 */
	private updateDocumentStatus(docUuid: string): void {
		const originalDoc = this._operation.config?.docs?.find((doc: IDocumentsDocs) => doc.uuid === docUuid);
		if (originalDoc) {
			const minFiles = this.getMinCount(originalDoc);
			const currentFileCount =
				this.selectedFiles[docUuid]?.filter((file) => file !== null && file !== undefined).length || 0;

			// Actualizar documentStatuses
			// Si no es requerido y no hay archivos, eliminar el status
			if (!originalDoc.required && currentFileCount === 0) {
				delete this.documentStatuses[docUuid];
			} else {
				this.documentStatuses[docUuid] =
					currentFileCount >= minFiles ||
					(originalDoc.type === 'IRPF' && this.formCVE.valid && this.formCVE.value)
						? 'OK'
						: 'KO';
			}
			this.documentServerErrors[docUuid] = false;

			// Buscar y actualizar el status del documento en docsConf
			const docConfig = this.docsConf.documents.find((doc) => doc.uuid === docUuid);
			if (docConfig) {
				if (currentFileCount >= minFiles && docConfig.status !== TestStatus.OnComplete) {
					// Se cumple la cantidad mínima, cambiar a OnComplete
					docConfig.status = TestStatus.OnComplete;
				} else if (currentFileCount < minFiles && docConfig.status === TestStatus.OnComplete) {
					// Ya no se cumple la cantidad mínima, volver a OnInit
					docConfig.status = TestStatus.OnInit;
				}
			}
		}
	}

	/**
	 * Actualiza los estados de los documentos basándose en las respuestas del servidor.
	 *
	 * @param successfulResponses Array de respuestas exitosas
	 * @param errorResponses Array de respuestas con errores
	 */
	private updateDocumentStatuses(
		successfulResponses: (IDocsResponse & { docUuid: string })[],
		errorResponses: { docUuid: string; isError: true; error: any }[],
	): void {
		// Procesar respuestas exitosas
		successfulResponses.forEach((response) => {
			if (response.result === OPERATION_STATUS.OK || response.result?.toUpperCase() === OPERATION_STATUS.OK) {
				this.documentStatuses[response.docUuid] = 'OK';
				this.documentServerErrors[response.docUuid] = false;
			} else {
				this.documentStatuses[response.docUuid] = 'KO';
				this.documentServerErrors[response.docUuid] = true;
			}
		});

		// Procesar respuestas con errores
		errorResponses.forEach((response) => {
			this.documentStatuses[response.docUuid] = 'KO';
			this.documentServerErrors[response.docUuid] = true;
		});
	}

	/**
	 * Obtiene el estado de un documento específico.
	 *
	 * @param docUuid UUID del documento
	 * @returns El estado del documento ('OK', 'KO', o null si no se ha procesado)
	 */
	public getDocumentStatus(docUuid: string): 'OK' | 'KO' | null {
		return this.documentStatuses[docUuid] || null;
	}

	/**
	 * Devuelve el control de formulario (`FormControl`) correspondiente al UUID y índice especificados.
	 *
	 * @param docUuid UUID del documento.
	 * @param index Índice del control dentro del FormArray del documento.
	 * @returns El `FormControl` asociado al UUID y índice dados.
	 */
	public getDocControl(docUuid: string, index: number): FormControl {
		return this.docControls[docUuid]?.at(index) as FormControl;
	}

	/**
	 * Devuelve el índice de un documento específico dentro del array de documentos de la configuración de la operación.
	 *
	 * @param doc El documento cuya posición se desea obtener.
	 * @returns El índice del documento en el array, o -1 si no se encuentra.
	 */
	public getDocIndex(doc: IDocumentsDocs): number {
		return this.operation.config.docs.indexOf(doc);
	}

	/**
	 * Cuenta cuántos archivos han sido subidos para un documento específico.
	 *
	 * @param doc El documento del cual contar los archivos subidos.
	 * @returns El número de archivos subidos para el documento.
	 */
	public getUploadedCount(doc: IDocumentsDocs): number {
		const files = this.selectedFiles[doc.uuid];
		if (!files) return 0;
		return files.filter((file) => file !== null).length;
	}

	/**
	 * Devuelve el número total de archivos que se pueden subir para un documento específico.
	 *
	 * @param doc El documento del cual obtener el total.
	 * @returns El número máximo de archivos que se pueden subir.
	 */
	public getTotalCount(doc: IDocumentsDocs): number {
		return doc?.max ?? 1;
	}

	/**
	 * Devuelve el número mínimo de archivos requeridos para un documento específico.
	 *
	 * @param doc El documento del cual obtener el mínimo.
	 * @returns El número mínimo de archivos requeridos.
	 */
	public getMinCount(doc: IDocumentsDocs): number {
		return doc?.min ?? (doc.required ? 1 : 0);
	}

	/**
	 * Devuelve el número máximo de archivos permitidos para un documento específico.
	 *
	 * @param doc El documento del cual obtener el máximo.
	 * @returns El número máximo de archivos permitidos.
	 */
	public getMaxCount(doc: IDocumentsDocs): number {
		return doc?.max ?? 1;
	}

	/**
	 * Verifica si se debe mostrar el texto de "Debes adjuntar al menos".
	 * Se muestra cuando el mínimo es mayor a 1 y es diferente del máximo.
	 *
	 * @param doc El documento a verificar.
	 * @returns True si se debe mostrar el texto de adjuntar.
	 */
	public shouldShowAttachmentText(doc: IDocumentsDocs): boolean {
		const minCount = this.getMinCount(doc);
		const maxCount = this.getMaxCount(doc);
		return minCount > 1 && maxCount > 1 && minCount !== maxCount;
	}

	/**
	 * Genera el texto mejorado sobre los archivos a adjuntar.
	 * Muestra rangos cuando min y max son diferentes.
	 *
	 * @param doc El documento del cual generar el texto.
	 * @returns El texto descriptivo sobre los archivos a adjuntar.
	 */
	public getAttachmentText(doc: IDocumentsDocs): string {
		const minCount = this.getMinCount(doc);
		if (minCount === 1) {
			return this.trans.instant('DOCUMENTS.ATTACHMENT_TEXT.SINGLE', { minCount });
		} else {
			return this.trans.instant('DOCUMENTS.ATTACHMENT_TEXT.MULTIPLE', { minCount });
		}
	}

	/**
	 * Combina la descripción del documento con el texto de adjuntar archivos.
	 * Simplifica la lógica del HTML al manejar todo en una sola función.
	 *
	 * @param doc El documento del cual generar el texto completo.
	 * @returns El texto completo que incluye descripción y requisitos de archivos.
	 */
	public getCompleteDocumentText(doc: IDocumentsDocs): string {
		let text = '';
		const customDescription = this.resolveText(doc?.description);

		const descKey = 'DOCUMENTS.DESCRIPTION.' + doc.type.toUpperCase();
		const defaultDesc = this.trans.instant(descKey);
		// No mutar doc.description con i18n: si no, el texto queda fijado al primer idioma.
		const descFromApi = customDescription || (typeof doc?.description === 'string' ? doc.description : '');
		const descFromI18n = !descFromApi && defaultDesc && defaultDesc !== descKey ? defaultDesc : '';
		const descriptionPart = descFromApi || descFromI18n;

		if (descriptionPart) text += descriptionPart;

		if (descriptionPart && this.shouldShowAttachmentText(doc)) {
			text += '. ';
		}

		if (this.shouldShowAttachmentText(doc)) text += this.getAttachmentText(doc);

		return text;
	}

	/**
	 * Obtiene las instrucciones para un documento específico.
	 * Si el documento no tiene instrucciones personalizadas, retorna las instrucciones por defecto según el tipo.
	 *
	 * @param doc El documento del cual obtener las instrucciones.
	 * @returns Las instrucciones del documento o null si no existen.
	 */
	public getDocumentInstructions(doc: IDocumentsDocs): string | null {
		// Si ya tiene instrucciones personalizadas, retornarlas
		const customInstructions = this.resolveText(doc?.instructions);
		if (customInstructions) return customInstructions;

		// Si no existe instrucciones, buscar instrucciones por defecto (si existen)
		const instructionsKey = 'DOCUMENTS.INSTRUCTIONS_DEFAULT.' + doc.type.toUpperCase();
		const instructions = this.trans.instant(instructionsKey);

		// Solo retornar si la traducción existe (si no existe, instant devuelve la misma key)
		if (instructions && instructions !== instructionsKey) return instructions;

		return null;
	}

	/**
	 * Verifica si un documento tiene instrucciones disponibles (personalizadas o por defecto).
	 *
	 * @param doc El documento a verificar.
	 * @returns True si el documento tiene instrucciones disponibles.
	 */
	public hasDocumentInstructions(doc: IDocumentsDocs): boolean {
		return this.getDocumentInstructions(doc) !== null;
	}

	/**
	 * Calcula el valor de progreso para un documento específico.
	 *
	 * @param doc El documento del cual calcular el progreso.
	 * @returns El porcentaje de progreso (0-100).
	 */
	public getProgressValue(doc: IDocumentsDocs): number {
		const total = this.getTotalCount(doc);
		if (total === 0) return 0;
		return (this.getUploadedCount(doc) / total) * 100;
	}

	/**
	 * Genera una etiqueta de progreso para un documento específico.
	 *
	 * @param doc El documento del cual generar la etiqueta.
	 * @returns Una cadena que describe el progreso actual.
	 */
	public getProgressLabel(doc: IDocumentsDocs): string {
		const total = this.getTotalCount(doc);
		const uploaded = this.getUploadedCount(doc);
		const remaining = total - uploaded;
		return this.trans.instant('DOCUMENTS.PROGRESS_LABEL', { remaining, total, uploaded });
	}

	/**
	 * Verifica si un documento cumple con los requisitos mínimos de archivos.
	 *
	 * @param doc El documento a verificar.
	 * @returns True si el documento cumple con los requisitos mínimos.
	 */
	public isDocumentValid(doc: IDocumentsDocs): boolean {
		const uploaded = this.getUploadedCount(doc);
		const minimum = this.getMinCount(doc);
		return uploaded >= minimum;
	}

	/**
	 * Verifica si todos los documentos cumplen con sus requisitos mínimos.
	 *
	 * @returns True si todos los documentos son válidos.
	 */
	public areAllDocumentsValid(): boolean {
		if (!this._operation.config?.docs) return false;
		return this._operation.config.docs.every((doc) => this.isDocumentValid(doc));
	}

	/**
	 * Verifica si todos los documentos requeridos cumplen con el mínimo de archivos.
	 * Los documentos no requeridos se ignoran en esta validación.
	 *
	 * @returns True si todos los documentos requeridos tienen el mínimo de archivos.
	 */
	public areRequiredDocumentsValid(): boolean {
		if (!this._operation.config?.docs) return true;

		const requiredDocs = this._operation.config.docs.filter((doc: IDocumentsDocs) => doc.required);
		if (requiredDocs.length === 0) return true; // Si no hay documentos requeridos, es válido

		return requiredDocs.every((doc: IDocumentsDocs) => {
			const uploaded = this.getUploadedCount(doc);
			const minimum = this.getMinCount(doc);
			return uploaded >= minimum || (doc.type === 'IRPF' && this.formCVE.valid && this.formCVE.value);
		});
	}

	/**
	 * Obtiene la lista de documentos que no cumplen con los requisitos mínimos.
	 *
	 * @returns Array de documentos inválidos.
	 */
	public getInvalidDocuments(): IDocumentsDocs[] {
		if (!this._operation.config?.docs) return [];
		return this._operation.config.docs.filter((doc) => !this.isDocumentValid(doc));
	}

	/**
	 * Verifica si un documento específico puede aceptar más archivos.
	 *
	 * @param doc El documento a verificar.
	 * @returns True si se pueden agregar más archivos.
	 */
	public canAddMoreFiles(doc: IDocumentsDocs): boolean {
		return this.getNextAvailableIndex(doc) !== -1;
	}

	/**
	 * Encuentra el primer índice disponible (null) en el array de archivos de un documento.
	 *
	 * @param doc El documento del cual encontrar el próximo índice disponible.
	 * @returns El índice disponible, o -1 si no hay slots disponibles.
	 */
	public getNextAvailableIndex(doc: IDocumentsDocs): number {
		const files = this.selectedFiles[doc.uuid];
		if (!files) return -1;

		for (let i = 0; i < files.length; i++) {
			if (files[i] === null) {
				return i;
			}
		}
		return -1; // No hay slots disponibles
	}

	/**
	 * Calcula el tamaño total en bytes de todos los archivos de un documento.
	 *
	 * @param docUuid UUID del documento.
	 * @returns Tamaño total en bytes de todos los archivos del documento.
	 */
	public getTotalFileSize(docUuid: string): number {
		const files = this.selectedFiles[docUuid];
		if (!files) return 0;

		return files.reduce((totalSize, file) => {
			return totalSize + (file?.size || 0);
		}, 0);
	}

	/**
	 * Verifica si agregar un nuevo archivo excedería el límite de tamaño máximo por documento.
	 *
	 * @param docUuid UUID del documento.
	 * @param newFile Archivo que se quiere agregar.
	 * @returns true si agregar el archivo excedería el límite, false si está dentro del límite.
	 */
	public wouldExceedSizeLimit(docUuid: string, newFile?: File | null): boolean {
		if (!newFile) return false;
		const currentSize = this.getTotalFileSize(docUuid);
		const newTotalSize = currentSize + newFile.size;
		return newTotalSize > MaxFileSizePerDocumentBytes;
	}

	/**
	 * Verifica si agregar múltiples archivos excedería el límite de tamaño máximo por documento.
	 *
	 * @param docUuid UUID del documento.
	 * @param newFiles Lista de archivos que se quieren agregar.
	 * @returns true si agregar todos los archivos excedería el límite, false si está dentro del límite.
	 */
	public wouldExceedSizeLimitMultiple(docUuid: string, newFiles: FileList): boolean {
		if (!newFiles || newFiles.length === 0) return false;

		const currentSize = this.getTotalFileSize(docUuid);
		let newFilesTotalSize = 0;

		// Calcular el tamaño total de los archivos que se intentan agregar
		for (let i = 0; i < newFiles.length; i++) {
			const file = newFiles.item(i);
			if (file) {
				newFilesTotalSize += file.size;
			}
		}

		return currentSize + newFilesTotalSize > MaxFileSizePerDocumentBytes;
	}

	/**
	 * Convierte bytes a megabytes con dos decimales.
	 *
	 * @param bytes Tamaño en bytes.
	 * @returns Tamaño en megabytes redondeado a dos decimales.
	 */
	public bytesToMB(bytes: number): number {
		return Math.round((bytes / (1024 * 1024)) * 100) / 100;
	}

	/**
	 * Trunca el nombre del archivo a un máximo de caracteres manteniendo la extensión visible.
	 *
	 * @param fileName Nombre del archivo completo.
	 * @param maxLength Longitud máxima permitida para el nombre sin extensión (por defecto 28).
	 * @returns Nombre del archivo truncado con elipsis si es necesario.
	 */
	public truncateFileName(fileName: string, maxLength: number = 28): string {
		if (!fileName || fileName.length <= maxLength) return fileName;

		// Obtener la extensión del archivo
		const lastDotIndex = fileName.lastIndexOf('.');
		const extension = lastDotIndex > 0 ? fileName.substring(lastDotIndex) : '';
		const nameWithoutExtension = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;

		// Si el nombre sin extensión es menor o igual al maxLength, devolver el nombre completo
		if (nameWithoutExtension.length <= maxLength) {
			return fileName;
		}

		// Truncar solo el nombre (sin extensión) al maxLength y agregar elipsis
		const truncatedName = nameWithoutExtension.substring(0, maxLength);
		return `${truncatedName}…${extension}`;
	}

	/**
	 * Formatea el tamaño del archivo en una unidad legible.
	 *
	 * @param bytes Tamaño en bytes.
	 * @returns Tamaño formateado con la unidad correspondiente.
	 */
	public formatFileSize(bytes: number): string {
		if (bytes === 0) return '0 B';

		const k = 1024;
		const sizes = ['B', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));

		const size = bytes / Math.pow(k, i);
		const formattedSize = i === 0 ? size : Math.round(size * 100) / 100;

		return `${formattedSize} ${sizes[i]}`;
	}

	/**
	 * Verifica si el tipo MIME del archivo está permitido.
	 *
	 * @param file Archivo a validar.
	 * @returns true si el tipo MIME está permitido, false si no lo está.
	 */
	public isFileExtensionValid(file: File): boolean {
		if (!file) return false;

		const allowedMimeTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

		return allowedMimeTypes.includes(file.type.toLowerCase());
	}

	/**
	 * Verifica si el tipo MIME del archivo es consistente con otros archivos del mismo documento.
	 *
	 * @param file Archivo a validar.
	 * @param docUuid UUID del documento.
	 * @returns true si el tipo MIME es consistente o si es el primer archivo, false si no lo es.
	 */
	public isExtensionConsistentWithDocument(file: File, docUuid: string): boolean {
		if (!file) return false;

		const existingFiles = this.selectedFiles[docUuid];
		if (!existingFiles) return true;

		// Buscar el primer archivo válido para obtener el tipo MIME de referencia
		const firstValidFile = existingFiles.find((f) => f !== null);
		if (!firstValidFile) return true; // Si no hay archivos existentes, cualquier tipo MIME válido es aceptable

		const newFileExtension = this.getFileExtensionFromMime(file);
		const existingExtension = this.getFileExtensionFromMime(firstValidFile);

		return newFileExtension === existingExtension;
	}

	/**
	 * Obtiene la extensión requerida para un documento basándose en los archivos ya adjuntados.
	 * Devuelve la extensión en formato legible para mostrar al usuario.
	 *
	 * @param docUuid UUID del documento.
	 * @returns La extensión requerida en formato legible (ej: ".pdf") o null si no hay archivos adjuntados.
	 */
	public getDocumentRequiredExtension(docUuid: string): string | null {
		const existingFiles = this.selectedFiles[docUuid];
		if (!existingFiles) return null;

		const firstValidFile = existingFiles.find((f) => f !== null);
		if (!firstValidFile) return null;

		return this.mimeTypeToDisplayExtension(firstValidFile.type);
	}

	/**
	 * Obtiene la extensión de un archivo basándose en su tipo MIME.
	 *
	 * @param file El objeto File del cual obtener la extensión.
	 * @returns La extensión del archivo en minúsculas.
	 */
	private getFileExtensionFromMime(file: File): string {
		const mimeToExtension: { [key: string]: string } = {
			'application/pdf': '.pdf',
			'image/jpeg': '.jpg',
			'image/jpg': '.jpg',
			'image/png': '.png',
		};

		return mimeToExtension[file.type.toLowerCase()] || '';
	}

	/**
	 * Convierte un tipo MIME a una extensión legible para mostrar al usuario.
	 *
	 * @param mimeType El tipo MIME a convertir.
	 * @returns La extensión legible (ej: ".pdf", ".jpg", ".png").
	 */
	private mimeTypeToDisplayExtension(mimeType: string): string {
		const mimeToExtension: { [key: string]: string } = {
			'application/pdf': '.pdf',
			'image/jpeg': '.jpg',
			'image/jpg': '.jpg',
			'image/png': '.png',
		};

		return mimeToExtension[mimeType.toLowerCase()] || mimeType;
	}

	/**
	 * Obtiene las extensiones permitidas en formato legible para mostrar al usuario.
	 *
	 * @returns Array de extensiones legibles (ej: [".pdf", ".jpg", ".png"]).
	 */
	public getAllowedExtensionsForDisplay(): string[] {
		if (this._operation.config.docs[0].type === 'SCRIPTURE') {
			return ['.pdf'];
		}
		return ['.pdf', '.jpg', '.jpeg', '.png'];
	}

	/**
	 * Obtiene los tipos MIME permitidos para un documento específico.
	 * Si ya hay archivos adjuntados, solo permite el mismo tipo MIME.
	 * Si no hay archivos, permite todos los tipos válidos.
	 *
	 * @param docUuid UUID del documento.
	 * @returns String con los tipos MIME permitidos separados por comas.
	 */
	private getAcceptTypesForDocument(docUuid: string): string {
		const existingFiles = this.selectedFiles[docUuid];
		if (!existingFiles) {
			// Si no hay archivos, permitir todos los tipos válidos
			return 'application/pdf,image/jpeg,image/jpg,image/png';
		}

		// Buscar el primer archivo válido para obtener el tipo MIME
		const firstValidFile = existingFiles.find((f) => f !== null);
		if (!firstValidFile) {
			// Si no hay archivos válidos, permitir todos los tipos
			return 'application/pdf,image/jpeg,image/jpg,image/png';
		}

		// Solo permitir el mismo tipo MIME que el archivo existente
		return firstValidFile.type;
	}

	/**
	 * Obtiene información sobre el uso de espacio de un documento.
	 *
	 * @param docUuid UUID del documento.
	 * @returns Objeto con información del uso de espacio del documento.
	 */
	public getDocumentSizeInfo(docUuid: string): {
		currentMB: number;
		maxMB: number;
		remainingMB: number;
		percentage: number;
	} {
		const currentBytes = this.getTotalFileSize(docUuid);
		const currentMB = this.bytesToMB(currentBytes);
		const maxMB = MaxFileSizePerDocumentMB;
		const remainingMB = Math.max(0, maxMB - currentMB);
		const percentage = Math.min(100, (currentMB / maxMB) * 100);

		return {
			currentMB,
			maxMB,
			remainingMB,
			percentage,
		};
	}

	/**
	 * Verifica si un documento tiene archivos PDF adjuntos.
	 *
	 * @param doc El documento a verificar.
	 * @returns True si el documento tiene al menos un archivo PDF.
	 */
	public hasPdfFiles(doc: IDocumentsDocs): boolean {
		const files = this.selectedFiles[doc.uuid];
		if (!files) return false;
		return files.some((file) => file && file.name.toLowerCase().endsWith('.pdf'));
	}

	/**
	 * Simula el clic en el input de archivo para permitir al usuario seleccionar archivos adicionales.
	 *
	 * @param doc El documento para el cual se desea agregar archivos.
	 */
	public triggerFileUpload(doc: IDocumentsDocs): void {
		const nextIndex = this.getNextAvailableIndex(doc);
		if (nextIndex !== -1) {
			const control = this.getDocControl(doc.uuid, nextIndex);
			if (control) {
				// Crear un input temporal de tipo file
				const input = document.createElement('input');
				input.type = 'file';

				// Determinar las extensiones permitidas para el input
				const existingExtension = this.getDocumentRequiredExtension(doc.uuid);
				if (existingExtension) {
					input.accept = existingExtension;
				} else {
					const acceptedExtensions = this.getAllowedExtensionsForDisplay();
					input.accept = acceptedExtensions.join(',');
				}

				// Permitir múltiples archivos si hay más de un slot disponible
				const currentFiles = this.getUploadedCount(doc);
				const maxFiles = this.getMaxCount(doc);
				const availableSlots = maxFiles - currentFiles;
				input.multiple = availableSlots > 1;

				input.onchange = (event: any) => {
					const files: FileList = event.target.files;
					this.handleFileInput(files, doc.uuid, nextIndex);
				};

				// Simular el clic
				input.click();
			}
		}
	}

	public triggerTakePhoto(doc: IDocumentsDocs): void {
		const requiredExt = this.getDocumentRequiredExtension(doc.uuid);
		const fileExtension = requiredExt ? requiredExt.replace('.', '') : 'jpg';
		const faceSlot = this.getDriveLicenseFaceSlot(doc);
		this.photoCaptureConfig = {
			doc,
			fileExtension,
			...(faceSlot !== undefined ? { faceSlot } : {}),
		};
		this.isVisibleCapture = true;
	}

	/**
	 * Índice de cara para carnet (0 delantera, 1 trasera) cuando hace falta más de una foto.
	 */
	private getDriveLicenseFaceSlot(doc: IDocumentsDocs): 0 | 1 | undefined {
		if (doc.type !== DOCS_TYPE.DRIVE_LICENSE || this.getMaxCount(doc) <= 1) {
			return undefined;
		}
		// Slot por índice del hueco libre (0 anverso, 1 reverso), no por conteo de fotos (NIF-999).
		const nextIndex = this.getNextAvailableIndex(doc);
		if (nextIndex !== 0 && nextIndex !== 1) {
			return undefined;
		}
		return nextIndex;
	}

	private exitPhotoCapture(): void {
		this.photoCaptureConfig = null;
		this.isVisibleCapture = false;
	}

	/**
	 * Alterna la expansión de un documento identificado por su UUID.
	 * Si el documento ya está expandido, lo colapsa; si no, lo expande.
	 *
	 * @param uuid - El identificador único (UUID) del documento a expandir o colapsar.
	 */
	public toggleDocumentExpansion(uuid: string, expanded: boolean): void {
		this.expandedDocUuid = expanded ? uuid : null;
	}

	/**
	 * Navega al siguiente documento en la lista, expandiéndolo y colapsando el actual.
	 *
	 * @param currentIndex - El índice actual del documento en la lista.
	 */
	public goToNextDocument(currentIndex: number): void {
		const nextIndex = currentIndex + 1;
		if (nextIndex < this._operation.config.docs.length) {
			this.expandedDocUuid = this._operation.config.docs[nextIndex].uuid;
		}
	}
	// #endregion

	// #region Photo
	public async onPhotoTaken(event: IDocsPhotoTakenEvent): Promise<void> {
		if (event.permissionKo) {
			await this.handlePhotoCapturePermissionKo(event.docUuid, event.debugImage);
			return;
		}

		if (!event.file) return;

		const { file, docUuid } = event;

		this.closeAlert(docUuid);

		const previousCapture = this.photoCaptureConfig;

		const docConfig = this._operation.config?.docs?.find((doc) => doc.uuid === docUuid);
		if (!docConfig) {
			this.exitPhotoCapture();
			return;
		}

		const maxFiles = this.getMaxCount(docConfig);
		const currentFiles = this.getUploadedCount(docConfig);

		if (currentFiles >= maxFiles) {
			this.showMaxFilesError(docUuid, maxFiles);
			return;
		}

		const nextAvailableIndex = this.getNextAvailableIndex(docConfig);
		if (nextAvailableIndex === -1) {
			this.showMaxFilesError(docUuid, maxFiles);
			return;
		}

		if (!this.isFileExtensionValid(file)) {
			this.showFileExtensionError(docUuid);
			return;
		}

		if (!this.isExtensionConsistentWithDocument(file, docUuid)) {
			this.showExtensionConsistencyError(docUuid, this.getDocumentRequiredExtension(docUuid));
			return;
		}

		if (this.wouldExceedSizeLimit(docUuid, file)) {
			this.showFileSizeError(docUuid);
			return;
		}

		if (this.selectedFiles[docUuid] && nextAvailableIndex < this.selectedFiles[docUuid].length) {
			this.selectedFiles[docUuid][nextAvailableIndex] = file;
		}

		if (this.docControls[docUuid] && this.docControls[docUuid].at(nextAvailableIndex)) {
			this.docControls[docUuid].at(nextAvailableIndex).setValue(file);
		}

		const control = this.getDocControl(docUuid, nextAvailableIndex);
		if (control) control.setValue(this.selectedFiles[docUuid].filter((f) => f !== null));

		this.updateDocumentStatus(docUuid);

		const fileExtension =
			previousCapture?.fileExtension ?? this.getDocumentRequiredExtension(docUuid)?.replace('.', '') ?? 'jpg';

		const shouldContinueDriveLicense =
			docConfig.type === DOCS_TYPE.DRIVE_LICENSE && maxFiles > 1 && this.getUploadedCount(docConfig) < maxFiles;

		if (shouldContinueDriveLicense) {
			const nextFaceSlot = this.getDriveLicenseFaceSlot(docConfig);
			this.photoCaptureConfig = {
				doc: docConfig,
				fileExtension,
				...(nextFaceSlot !== undefined ? { faceSlot: nextFaceSlot } : {}),
			};
			this.cdr.detectChanges();
			this.docsTakePhotoComponent?.prepareNextFace();
			return;
		}

		this.exitPhotoCapture();
	}
	// #endregion

	// #region Loading
	/**
	 * Muestra un indicador de carga personalizado según la configuración de la operación.
	 * Si la operación utiliza un iframe, muestra el indicador de carga en el iframe;
	 * de lo contrario, muestra el indicador de carga estándar.
	 *
	 * @param text Texto opcional que se mostrará junto al indicador de carga.
	 */
	private showLoading(text = ''): void {
		this.operation.config.iframe ? this.loadingService.showIframe(text) : this.loadingService.show(text);
	}

	/**
	 * Oculta el indicador de carga personalizado.
	 * Si la configuración de la operación utiliza un iframe, oculta el indicador de carga específico para iframes;
	 * de lo contrario, oculta el indicador de carga estándar.
	 */
	private hideLoading(): void {
		this.operation.config.iframe ? this.loadingService.hideIframe() : this.loadingService.hide();
	}
	// #endregion

	// #region Help
	/**
	 * Maneja los cambios de ayuda de navegación según la acción recibida.
	 * Ejecuta la función correspondiente dependiendo del tipo de acción:
	 * - QR: Muestra el QR.
	 * - HELP: Muestra una advertencia de ayuda.
	 *
	 * @param action La acción de ayuda de navegación a procesar.
	 */
	public navHelpChange(action: INavHelp): void {
		const actions = {
			[NavHelpAction.QR]: () => (this.isVisibleScan = true),
			[NavHelpAction.LANGUAGE]: () => this.languagePickerUi.open(),
			[NavHelpAction.HELP]: () => this.displayHelpWarning(true),
		};
		return actions[action.id]();
	}

	/**
	 * Muestra el cuadro de diálogo para cambiar de dispositivo.
	 * Establece la propiedad `isVisibleChangeDevice` a `true` para hacer visible la interfaz correspondiente.
	 */
	public changeDevice(): void {
		this.isVisibleChangeDevice = true;
		// Detener el temporizador de inactividad al intentar cambiar de dispositivo
		this.stopInactivityTimer();
	}

	public restartInactivityTimerOnClose(): void {
		// Reiniciar el temporizador de inactividad al cerrar el cambio de dispositivo si está en la página de ayuda
		if (this.pageWarning.type === PAGE_WARNING.HELP) this.startInactivityTimer();
	}

	public openDocumentHelp(): void {
		this.setPageHelp('additional_info');
		this.stopTotalTime();
	}

	private setPageHelp(type: 'help' | 'additional_info' = 'help'): void {
		const isHelpDocs = type === 'help';
		this.isHelpAdditionalInfo = !isHelpDocs;

		this.helpTitle = isHelpDocs ? 'DOCUMENTS.HELP.TITLE' : 'DOCUMENTS.HELP.TITLE_ADDITIONAL_INFO';
		this.setPageWarning(PAGE_WARNING.HELP);

		this.updateSubtitle();
	}
	// #endregion

	// #region Tutorial
	/**
	 * Finaliza el tutorial del usuario.
	 *
	 * Esta función establece la configuración del tutorial en falso, envía registros en la nube
	 * indicando que el tutorial se completó correctamente y actualiza el subtítulo.
	 */
	public finishTutorial(): void {
		this.operation.config.tutorial = false;
		this.updateSubtitle();
	}
	// #endregion

	// #region Textos
	/**
	 * Actualiza el subtítulo del componente.
	 *
	 * Este método asigna el valor generado por el método `generateSubtitle()` a la propiedad `subtitle`.
	 */
	public updateSubtitle(): void {
		this.subtitle = this.generateSubtitle();
	}

	/**
	 * Genera el subtítulo para la página de documentos basado en la configuración de la operación,
	 * el tipo de advertencia de página y el número total de documentos.
	 *
	 * @returns {string} El subtítulo traducido para la página de documentos.
	 */
	private generateSubtitle(): string {
		let subtitle = '';

		if (this.pageWarning.type === PAGE_WARNING.HELP) {
			subtitle = this.isHelpAdditionalInfo
				? 'DOCUMENTS.HELP.DESCRIPTION_ADDITIONAL_INFO'
				: 'DOCUMENTS.HELP.DESCRIPTION';
		} else if (this._operation?.config?.tutorial) {
			if (this.hasAttorneyPowersLikeDocs()) return this.trans.instant('CONNECTIONS.SCRIPTURE.TUTORIAL_TITLE');
			if (this.totalDocCount === 1) {
				return this.trans.instant('DOCUMENTS.TUTORIAL.TITLE_SINGLE', {
					documentType: this.getDocumentTitle(this._operation.config.docs[0]),
				});
			}
			subtitle = 'DOCUMENTS.TUTORIAL.TITLE';
		} else if (this.pageWarning.show) {
			return '';
		} else {
			// Si hay un título personalizado en la configuración de la operación, usarlo
			const customTitle = this.resolveText(this._operation?.config?.title);
			if (customTitle) {
				return customTitle;
			}

			subtitle = this.totalDocCount === 1 ? 'DOCUMENTS.SUBTITLE.SINGLE' : 'DOCUMENTS.SUBTITLE.MULTIPLE';
		}

		return this.trans.instant(subtitle);
	}

	/**
	 * Obtiene el título del documento. Si el documento tiene un título definido, lo devuelve; de lo contrario, devuelve una traducción del tipo de documento.
	 * @param doc El objeto documento del tipo IDocumentsDocs.
	 * @returns El título del documento como cadena de texto.
	 */
	private getDocumentTitle(doc: IDocumentsDocs): string {
		const customTitle = this.resolveText(doc?.title);
		if (customTitle) return customTitle;
		return doc?.type === 'DRIVE_LICENSE'
			? this.trans.instant('DOCUMENTS.TYPES.' + doc?.type).toLowerCase()
			: this.trans.instant('DOCUMENTS.TYPES.' + doc?.type);
	}

	public getDisplayDocumentTitle(doc: IDocumentsDocs): string {
		return this.resolveText(doc?.title) || this.trans.instant('DOCUMENTS.TYPES.' + doc?.type);
	}

	public resolveText(value?: LocalizedText | null): string {
		return this.languageService.resolveLocalizedText(value);
	}

	private applyLocalizedConfigText(): void {
		if (!this._operation?.config) return;

		if (this.operationTitleSource === undefined) {
			this.operationTitleSource = this._operation.config.title;
		}
		const resolvedTitle = this.resolveText(this.operationTitleSource);
		if (resolvedTitle) this._operation.config.title = resolvedTitle;

		this._operation.config?.docs?.forEach((doc) => {
			if (!this.localizedDocSource[doc.uuid]) {
				this.localizedDocSource[doc.uuid] = {
					title: doc.title,
					description: doc.description,
					instructions: doc.instructions,
					instructionsPhoto: doc.instructions_photo,
				};
			}
			const source = this.localizedDocSource[doc.uuid];
			const title = this.resolveText(source.title);
			if (title) doc.title = title;

			const description = this.resolveText(source.description);
			if (description) doc.description = description;

			const instructions = this.resolveText(source.instructions);
			if (instructions) doc.instructions = instructions;

			const instructionsPhoto = this.resolveText(source.instructionsPhoto);
			if (instructionsPhoto) doc.instructions_photo = instructionsPhoto;
		});
	}
	// #endregion

	/**
	 * Indica si todos los documentos de la operación son obligatorios.
	 * Si se proporciona una operación opcionalmente, la usa; en caso contrario
	 * usa la operación actual del componente.
	 */
	public areAllDocsRequired(operation?: IOperation): boolean {
		const op = (operation as IDocumentsOperation) ?? (this._operation as IDocumentsOperation);
		if (!op || !op.config || !op.config.docs) return false;
		return op.config.docs.every((doc) => doc.required);
	}

	// #region Warning
	/**
	 * Muestra una advertencia de ayuda en la página y registra observaciones en los logs si se proporcionan.
	 *
	 * @param isHelpByNavHelp Indica si la ayuda se muestra a través de la navegación de ayuda.
	 */
	private displayHelpWarning(isHelpByNavHelp: boolean): void {
		this.isHelpByNavHelp = isHelpByNavHelp;
		this.setPageHelp('help');
		this.stopTotalTime();

		// Iniciar temporizador de inactividad solo cuando se muestra la ayuda
		this.startInactivityTimer();
	}

	/**
	 * Establece el tipo y la visibilidad de la advertencia de página.
	 *
	 * @param type El tipo de advertencia de página a mostrar, o `null` para eliminar la advertencia.
	 * @param show Indica si se debe mostrar la advertencia. Por defecto es `true`.
	 */
	public setPageWarning(type: PAGE_WARNING | null, show: boolean = true): void {
		this.pageWarning = { show, type };

		this.updateSubtitle();
	}

	/**
	 * Muestra un error cuando se excede el límite de tamaño de archivos por documento.
	 *
	 * @param docUuid UUID del documento.
	 */
	private showFileSizeError(docUuid: string): void {
		const maxSizeMB = MaxFileSizePerDocumentMB;
		const message = this.trans.instant('DOCUMENTS.ERROR.FILE_SIZE_EXCEEDED', { maxSize: maxSizeMB });

		// Inicializar el objeto del documento si no existe
		if (!this.fileSizeAlerts[docUuid]) {
			this.fileSizeAlerts[docUuid] = { show: false, message: '' };
		}

		this.fileSizeAlerts[docUuid].message = message;
		this.fileSizeAlerts[docUuid].show = true;
	}

	/**
	 * Muestra un error cuando la extensión del archivo no está permitida.
	 *
	 * @param docUuid UUID del documento.
	 */
	private showFileExtensionError(docUuid: string): void {
		const message = this.trans.instant('DOCUMENTS.ERROR.FILE_EXTENSION_INVALID');

		// Inicializar el objeto del documento si no existe
		if (!this.fileExtensionAlerts[docUuid]) {
			this.fileExtensionAlerts[docUuid] = { show: false, message: '' };
		}

		this.fileExtensionAlerts[docUuid].message = message;
		this.fileExtensionAlerts[docUuid].show = true;
	}

	/**
	 * Muestra un error cuando la extensión del archivo no es consistente con otros archivos del documento.
	 * Utiliza la misma estructura de alertas que los errores de extensión general para simplificar el código.
	 *
	 * @param docUuid UUID del documento.
	 * @param requiredExtension La extensión requerida para mantener consistencia.
	 */
	private showExtensionConsistencyError(docUuid: string, requiredExtension: string | null): void {
		const message = requiredExtension
			? this.trans.instant('DOCUMENTS.ERROR.EXTENSION_CONSISTENCY', { extension: requiredExtension })
			: this.trans.instant('DOCUMENTS.ERROR.FILE_EXTENSION_INVALID');

		// Inicializar el objeto del documento si no existe
		if (!this.fileExtensionAlerts[docUuid]) {
			this.fileExtensionAlerts[docUuid] = { show: false, message: '' };
		}

		this.fileExtensionAlerts[docUuid].message = message;
		this.fileExtensionAlerts[docUuid].show = true;
	}

	/**
	 * Muestra un error cuando se excede el número máximo de archivos permitidos por documento.
	 *
	 * @param docUuid UUID del documento.
	 * @param maxFiles Número máximo de archivos permitidos.
	 */
	private showMaxFilesError(docUuid: string, maxFiles: number): void {
		const message = this.trans.instant('DOCUMENTS.ERROR.MAX_FILES_EXCEEDED', { maxFiles });

		// Inicializar el objeto del documento si no existe
		if (!this.fileExtensionAlerts[docUuid]) {
			this.fileExtensionAlerts[docUuid] = { show: false, message: '' };
		}

		this.fileExtensionAlerts[docUuid].message = message;
		this.fileExtensionAlerts[docUuid].show = true;
	}

	/**
	 * Muestra un error cuando no todos los archivos seleccionados se pudieron añadir.
	 *
	 * @param docUuid UUID del documento.
	 * @param total Número total de archivos seleccionados.
	 * @param processed Número de archivos procesados exitosamente.
	 */
	private showNotAllFilesAddedError(docUuid: string, total: number, processed: number): void {
		const message = this.trans.instant('DOCUMENTS.ERROR.NOT_ALL_FILES_ADDED', { total, processed });

		if (!this.notAllFilesAddedAlerts[docUuid]) {
			this.notAllFilesAddedAlerts[docUuid] = { show: false, message: '' };
		}

		this.notAllFilesAddedAlerts[docUuid].message = message;
		this.notAllFilesAddedAlerts[docUuid].show = true;
	}

	/**
	 * Maneja el cierre de alerts de tamaño de archivo y extensión.
	 */
	public closeAlert(docUuid?: string): void {
		if (docUuid) {
			// Cerrar alertas de un documento específico
			if (this.fileSizeAlerts[docUuid]) {
				this.fileSizeAlerts[docUuid].show = false;
			}
			if (this.fileExtensionAlerts[docUuid]) {
				this.fileExtensionAlerts[docUuid].show = false;
			}
			if (this.tooManyFilesAlerts[docUuid]) {
				this.tooManyFilesAlerts[docUuid].show = false;
			}
			if (this.notAllFilesAddedAlerts[docUuid]) {
				this.notAllFilesAddedAlerts[docUuid].show = false;
			}
		} else {
			// Cerrar todas las alertas
			Object.keys(this.fileSizeAlerts).forEach((uuid) => {
				this.fileSizeAlerts[uuid].show = false;
			});
			Object.keys(this.fileExtensionAlerts).forEach((uuid) => {
				this.fileExtensionAlerts[uuid].show = false;
			});
			Object.keys(this.tooManyFilesAlerts).forEach((uuid) => {
				this.tooManyFilesAlerts[uuid].show = false;
			});
			Object.keys(this.notAllFilesAddedAlerts).forEach((uuid) => {
				this.notAllFilesAddedAlerts[uuid].show = false;
			});
		}
	}

	/**
	 * Maneja el cierre específico del alert de tamaño de archivo.
	 */
	public closeSizeAlert(docUuid: string): void {
		if (this.fileSizeAlerts[docUuid]) {
			this.fileSizeAlerts[docUuid].show = false;
		}
	}

	/**
	 * Maneja el cierre específico del alert de extensión de archivo.
	 */
	public closeExtensionAlert(docUuid: string): void {
		if (this.fileExtensionAlerts[docUuid]) {
			this.fileExtensionAlerts[docUuid].show = false;
		}
	}

	/**
	 * Maneja el clic en botones del alert de tamaño de archivo.
	 */
	public buttonClicked(): void {
		// Por ahora solo cerramos el alert
		this.closeAlert();
	}
	// #endregion

	// #region Timers
	/**
	 * Inicia el temporizador total utilizando el servicio TimerManagerService.
	 * Llama al método `startTotalTime()` del servicio para comenzar a contar el tiempo total.
	 */
	private startTotalTime(): void {
		this.timerManagerService.startTotalTime();
	}

	/**
	 * Detiene el temporizador total utilizando el servicio TimerManagerService.
	 * Llama al método `stopTotalTime()` del servicio para finalizar el conteo de tiempo total.
	 */
	private stopTotalTime(): void {
		this.timerManagerService.stopTotalTime();
	}

	/**
	 * Calcula el tiempo máximo total basado en 2 minutos por cada archivo mínimo requerido.
	 * @returns Tiempo máximo en segundos
	 */
	private calculateMaxTime(): number {
		if (!this._operation?.config?.docs) return DOCS_MAX_TIME_PERFILE_SECONDS;

		const totalMinFiles = this._operation.config.docs.reduce((total, doc) => {
			return total + this.getMinCount(doc);
		}, 0);

		// Mínimo de un archivo (2 minutos) incluso si no hay documentos requeridos
		const minFiles = Math.max(1, totalMinFiles);
		return minFiles * DOCS_MAX_TIME_PERFILE_SECONDS * 1000;
	}

	/**
	 * Inicia el temporizador de tiempo máximo basado en archivos requeridos.
	 * Cuando se cumple el tiempo, envía un resultado negativo.
	 */
	private startMaxTimeTimer(): void {
		const maxTimeSeconds = this.calculateMaxTime();
		this.timerManagerService.startMaxTime(() => {
			this.stopTotalTime();
			// Enviar resultado negativo por timeout
			this.sendResult(false, 'max_time');
		}, maxTimeSeconds);
	}

	/**
	 * Detiene y limpia el temporizador de tiempo máximo.
	 */
	private stopMaxTimeTimer(): void {
		this.timerManagerService.stopMaxTime();
	}

	/**
	 * Inicia el temporizador de inactividad para detectar cuando el usuario excede el tiempo máximo permitido.
	 * Utiliza el valor MaxTime (300 segundos) para configurar el timeout.
	 * Cuando se cumple el tiempo, muestra la página de advertencia de timeout de inactividad.
	 */
	private startInactivityTimer(): void {
		this.stopInactivityTimer(); // Detener cualquier timer previo

		this.inactivityTimer = timer(DOCS_MAX_HELP_TIME * 1000).subscribe(() => {
			this.setPageWarning(PAGE_WARNING.INACTIVITY_TIMEOUT);
			this.stopTotalTime();
			// Enviar resultado negativo
			timer(2000).subscribe(() => {
				this.sendResult(false, 'help_timeout');
			});
		});
	}

	/**
	 * Detiene y limpia el temporizador de inactividad.
	 */
	private stopInactivityTimer(): void {
		if (this.inactivityTimer) {
			this.inactivityTimer.unsubscribe();
			this.inactivityTimer = null;
		}
	}
	// #endregion

	// #region Modals
	/**
	 * Abre el modal de instrucciones para el documento seleccionado.
	 *
	 * Asigna el documento proporcionado a la propiedad `selectedDoc` y muestra el modal de instrucciones estableciendo `isVisibleInstructions` en `true`.
	 * Si el documento no tiene instrucciones personalizadas, se asignan las instrucciones por defecto.
	 *
	 * @param doc El documento seleccionado de tipo `IDocumentsDocs`.
	 */
	public openInstructionsModal(doc: IDocumentsDocs): void {
		// Crear una copia del documento para no modificar el original
		this.selectedDoc = { ...doc };

		// Si no tiene instrucciones, asignar las instrucciones por defecto
		if (!this.selectedDoc.instructions) {
			const defaultInstructions = this.getDocumentInstructions(doc);
			if (defaultInstructions) this.selectedDoc.instructions = defaultInstructions;
		}

		this.isVisibleInstructions = true;
		// Detener el temporizador de inactividad al abrir instrucciones
		this.stopInactivityTimer();
	}

	private refreshSelectedDocOnLanguageChange(): void {
		if (!this.isVisibleInstructions || !this.selectedDoc?.uuid) return;
		const updatedDoc = this._operation?.config?.docs?.find((doc) => doc.uuid === this.selectedDoc.uuid);
		if (!updatedDoc) return;

		this.selectedDoc = { ...updatedDoc };
		if (!this.selectedDoc.instructions) {
			const defaultInstructions = this.getDocumentInstructions(updatedDoc);
			if (defaultInstructions) this.selectedDoc.instructions = defaultInstructions;
		}
	}
	// #endregion

	public hasAttorneyPowersLikeDocs(): boolean {
		return Boolean(this.operation.config.docs.find((doc: IDocumentsDocs) => doc.type === DOCS_TYPE.SCRIPTURE));
	}
}
